import { useState } from 'react';
import { useBooks, useChapters, useCreateBook, useUpdateBook } from '@/api/use-books';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Library, Plus, ChevronLeft, BookOpen, Pencil, Download, LayoutGrid, List } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Book Cover Colors ─── */
const COVER_COLORS = [
  'from-indigo-500 to-indigo-700',
  'from-rose-500 to-pink-700',
  'from-emerald-500 to-emerald-700',
  'from-amber-500 to-orange-700',
  'from-cyan-500 to-blue-700',
  'from-purple-500 to-violet-700',
];

function getBookColor(id: string): string {
  let hash = 0;
  for (const ch of id) hash = ((hash << 5) - hash + ch.charCodeAt(0)) | 0;
  return COVER_COLORS[Math.abs(hash) % COVER_COLORS.length];
}

function getBookIcon(shortName?: string | null): string {
  if (!shortName) return '📖';
  const s = shortName.toLowerCase();
  if (s.includes('idiom') || s.includes('idm')) return '文A';
  if (s.includes('grammar')) return 'Gr';
  if (s.includes('vocab')) return 'Vc';
  return shortName.slice(0, 2).toUpperCase();
}

const LEVEL_BADGES: Record<string, string> = {
  A1: 'bg-green-100 text-green-700',
  A2: 'bg-green-100 text-green-700',
  B1: 'bg-blue-100 text-blue-700',
  B2: 'bg-blue-100 text-blue-700',
  C1: 'bg-purple-100 text-purple-700',
  C2: 'bg-purple-100 text-purple-700',
  ADVANCED: 'bg-pink-100 text-pink-700',
};

/* ─── Add Book Dialog ─── */
function AddBookDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createBook = useCreateBook();
  const [form, setForm] = useState({ title: '', short_name: '', notes: '' });

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    createBook.mutate(
      { title: form.title.trim(), short_name: form.short_name || undefined, notes: form.notes || undefined },
      { onSuccess: () => { setForm({ title: '', short_name: '', notes: '' }); onClose(); } },
    );
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add Book</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Title *</label>
            <Input placeholder="e.g. Vocabulary Builder" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Short Name</label>
            <Input placeholder="e.g. vocab_builder" value={form.short_name} onChange={e => setForm(f => ({ ...f, short_name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Notes</label>
            <Input placeholder="Optional description" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!form.title.trim() || createBook.isPending}>
            {createBook.isPending ? 'Adding...' : 'Add Book'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Chapter Table ─── */
const STATUS_BADGE: Record<string, string> = {
  done: 'bg-emerald-100 text-emerald-700',
  processing: 'bg-amber-100 text-amber-700',
  pending: 'bg-slate-100 text-slate-600',
  skipped: 'bg-red-100 text-red-600',
};

function ChapterView({ bookId, bookTitle, onBack }: { bookId: string; bookTitle: string; onBack: () => void }) {
  const { data: chapters, isLoading } = useChapters(bookId);

  const chs = chapters ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="text-lg font-semibold">Chapters: {bookTitle}</h2>
            <p className="text-sm text-muted-foreground">Showing all synced modules for this resource.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          const csv = ['#,Chapter,Pages,Status,Items']
            .concat(chs.map(c => `${c.chapter_number ?? ''},${c.chapter_name},${c.page_start ?? ''}-${c.page_end ?? ''},${c.status},${c.item_count}`))
            .join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `${bookTitle}-progress.csv`; a.click();
          URL.revokeObjectURL(url);
        }}>
          <Download className="w-4 h-4 mr-2" />
          Export Progress
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 bg-muted rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">#</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Chapter Name</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Pages</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Status</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Items</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase text-xs tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {chs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No chapters yet</td></tr>
              ) : chs.map(ch => (
                <tr key={ch.id} className="border-t hover:bg-muted/20">
                  <td className="px-4 py-3 text-muted-foreground">{String(ch.chapter_number ?? '').padStart(2, '0')}</td>
                  <td className="px-4 py-3 font-medium">{ch.chapter_name}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {ch.page_start != null && ch.page_end != null ? `${String(ch.page_start).padStart(2, '0')}-${ch.page_end}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className={cn('text-xs', STATUS_BADGE[ch.status] ?? '')}>
                      {ch.status === 'processing' ? '⟳ PROCESSING' : ch.status.toUpperCase()}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">{ch.item_count || '—'}</td>
                  <td className="px-4 py-3">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><Pencil className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Main Books Page ─── */
export default function Books() {
  const { data: books, isLoading } = useBooks();
  const [selectedBook, setSelectedBook] = useState<{ id: string; title: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [editBook, setEditBook] = useState<{ id: string; title: string; short_name: string; notes: string } | null>(null);
  const updateBook = useUpdateBook();

  if (selectedBook) {
    return <ChapterView bookId={selectedBook.id} bookTitle={selectedBook.title} onBack={() => setSelectedBook(null)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Your Library</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage and sync your pedagogical resources.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('grid')} className={cn('p-2', viewMode === 'grid' ? 'bg-muted' : 'hover:bg-muted/50')}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('list')} className={cn('p-2', viewMode === 'list' ? 'bg-muted' : 'hover:bg-muted/50')}>
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Book cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-40 bg-muted rounded-lg animate-pulse" />)}
        </div>
      ) : (books ?? []).length === 0 ? (
        <div className="py-20 text-center text-muted-foreground">
          <Library className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No books yet. Add your first book!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(books ?? []).map(book => (
            <BookCard key={book.id} book={book} onSelect={() => setSelectedBook({ id: book.id, title: book.title })} onEdit={(b) => setEditBook(b)} />
          ))}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setAddOpen(true)}
        className="fixed bottom-8 right-8 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:bg-primary/90 flex items-center justify-center transition-transform hover:scale-110 z-40"
      >
        <Plus className="w-6 h-6" />
      </button>
      <AddBookDialog open={addOpen} onClose={() => setAddOpen(false)} />

      {editBook && (
        <Dialog open={!!editBook} onOpenChange={(v) => !v && setEditBook(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Edit Book</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Title</label>
                <Input value={editBook.title} onChange={e => setEditBook(prev => prev ? { ...prev, title: e.target.value } : null)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Short Name</label>
                <Input value={editBook.short_name} onChange={e => setEditBook(prev => prev ? { ...prev, short_name: e.target.value } : null)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Notes</label>
                <Input value={editBook.notes} onChange={e => setEditBook(prev => prev ? { ...prev, notes: e.target.value } : null)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditBook(null)}>Cancel</Button>
              <Button onClick={() => {
                if (editBook) {
                  updateBook.mutate({ id: editBook.id, title: editBook.title, short_name: editBook.short_name, notes: editBook.notes }, {
                    onSuccess: () => setEditBook(null),
                  });
                }
              }}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function BookCard({ book, onSelect, onEdit }: { book: any; onSelect: () => void; onEdit: (b: { id: string; title: string; short_name: string; notes: string }) => void }) {
  const { data: chapters } = useChapters(book.id);
  const chs = chapters ?? [];
  const done = chs.filter(c => c.status === 'done').length;
  const total = chs.length;
  const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;
  const imported = chs.reduce((sum, c) => sum + (c.item_count ?? 0), 0);
  const skipped = chs.filter(c => c.status === 'skipped').length;

  return (
    <Card className="hover:shadow-md transition-shadow overflow-hidden">
      <CardContent className="p-0">
        <div className="flex">
          {/* Book Cover */}
          <div className={cn('w-28 min-h-[180px] bg-gradient-to-br flex flex-col items-center justify-center text-white shrink-0', getBookColor(book.id))}>
            <BookOpen className="w-8 h-8 mb-1 opacity-80" />
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
              {getBookIcon(book.short_name)}
            </span>
          </div>

          {/* Book Info */}
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold">{book.title}</h3>
                {book.short_name && <p className="text-xs text-muted-foreground">{book.short_name}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Badge className={cn('text-[10px]', LEVEL_BADGES[(book.level ?? '').toUpperCase()] ?? 'bg-blue-100 text-blue-700')}>{book.level ? `${book.level} LEVEL` : 'N/A'}</Badge>
                <Button variant="ghost" size="icon" className="w-7 h-7" onClick={(e) => { e.stopPropagation(); onEdit({ id: book.id, title: book.title, short_name: book.short_name ?? '', notes: book.notes ?? '' }); }}><Pencil className="w-3.5 h-3.5" /></Button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span className="uppercase font-medium tracking-wider">Chapters Progress</span>
                <span>{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="h-1.5" />
            </div>

            {/* Stats */}
            <div className="flex gap-4 mt-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Imported</p>
                <p className="text-lg font-bold">{imported.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Skipped</p>
                <p className="text-lg font-bold">{skipped}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" className="flex-1" onClick={onSelect}>
                View Chapters
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
